/**
 * Codes-barres de la caisse (plan, sections 0 et 1.1) : UPC/EAN, et EAN-13 a
 * prix integre des produits au poids.
 *
 * Porte depuis backend/src/utils/weightBarcode.js et la cle GS1 de
 * backend/src/utils/gs1Parser.js, avec les MEMES vecteurs de test
 * (domain/__tests__/weightBarcode.vectors.js, copie de
 * backend/tests/fixtures/weightBarcode.vectors.js, comparee au fichier du
 * backend quand il est present).
 *
 * Etiquette au poids : 13 chiffres =
 *   [prefixe, 2 chiffres][SKU au poids, 5 chiffres][prix en cents, 5 chiffres][cle GS1]
 * Le prefixe vient du catalogue (catalog_meta.weight_barcode_prefix, '20'
 * pour le backend). Un prefixe d'un seul chiffre (« 2 », toute la plage
 * GS1 20 a 29) est accepte : les positions du SKU et du prix ne changent pas.
 */

export const PREFIXE_DEFAUT = '20';
export const PRIX_MAX_CENTS = 99999;
export const PRIX_MIN_CENTS = 1;
const SKU_POIDS = /^\d{5}$/;

export class CodeBarresError extends Error {
    constructor(message, code) {
        super(message);
        this.name = 'CodeBarresError';
        this.code = code;
    }
}

/** Cle GS1 (modulo 10) des chiffres donnes, sans la cle. Ponderation 3,1,3... depuis la droite. */
export function cleGs1(chiffresSansCle) {
    if (typeof chiffresSansCle !== 'string' || !/^\d+$/.test(chiffresSansCle)) return null;
    let somme = 0;
    for (let i = 0; i < chiffresSansCle.length; i++) {
        const poids = (chiffresSansCle.length - i) % 2 === 0 ? 1 : 3;
        somme += Number(chiffresSansCle[i]) * poids;
    }
    return (10 - (somme % 10)) % 10;
}

/** GTIN-8, UPC-A (12), EAN-13 ou GTIN-14 dont la cle est juste. */
export function cleGs1Valide(code) {
    if (typeof code !== 'string' || !/^\d+$/.test(code) || ![8, 12, 13, 14].includes(code.length)) return false;
    return cleGs1(code.slice(0, -1)) === Number(code[code.length - 1]);
}

function prefixeValide(prefixe) {
    return typeof prefixe === 'string' && /^\d{1,2}$/.test(prefixe);
}

/**
 * Genere l'EAN-13 a prix integre (memes erreurs et codes que le backend).
 * @param {string} skuPoids 5 chiffres
 * @param {number} prixCents entier de 1 a 99 999
 * @param {string} [prefixe] 2 chiffres
 */
export function genererEan13PrixIntegre(skuPoids, prixCents, prefixe = PREFIXE_DEFAUT) {
    if (typeof skuPoids !== 'string' || !SKU_POIDS.test(skuPoids)) {
        throw new CodeBarresError('SKU au poids invalide (5 chiffres requis)', 'INVALID_WEIGHT_SKU');
    }
    if (!Number.isInteger(prixCents)) throw new CodeBarresError('Prix invalide (entier en cents requis)', 'INVALID_PRICE');
    if (prixCents < PRIX_MIN_CENTS) throw new CodeBarresError('Prix invalide (minimum 0.01 $)', 'PRICE_TOO_LOW');
    if (prixCents > PRIX_MAX_CENTS) {
        throw new CodeBarresError('Le prix dépasse la limite encodable (999.99 $). Vendez l\'article comme prix fixe.', 'PRICE_TOO_HIGH');
    }
    if (typeof prefixe !== 'string' || !/^\d{2}$/.test(prefixe)) throw new CodeBarresError('Prefixe invalide', 'INVALID_PREFIX');
    const sansCle = `${prefixe}${skuPoids}${String(prixCents).padStart(5, '0')}`;
    return `${sansCle}${cleGs1(sansCle)}`;
}

/**
 * Lit un EAN-13 a prix integre.
 * @returns {{ weightSku: string, priceCents: number, valid: boolean } | null}
 *   null si ce n'est pas une etiquette au poids ; valid=false si la cle est fausse.
 */
export function lireEan13PrixIntegre(code, prefixe = PREFIXE_DEFAUT) {
    if (typeof code !== 'string' || !/^\d{13}$/.test(code)) return null;
    if (!prefixeValide(prefixe) || !code.startsWith(prefixe)) return null;
    return {
        weightSku: code.substring(2, 7),
        priceCents: Number.parseInt(code.substring(7, 12), 10),
        valid: cleGs1Valide(code),
    };
}

/**
 * Prix en cents d'apres le poids (kg) et le prix au kg (dollars), arrondi au
 * cent : meme calcul que le backend (computePriceCents), pour la parite des
 * vecteurs. La caisse ne s'en sert pas pour vendre : le prix vient de
 * l'etiquette.
 */
export function prixDepuisPoids(poidsKg, prixAuKg) {
    if (typeof poidsKg !== 'number' || !Number.isFinite(poidsKg) || poidsKg <= 0) {
        throw new CodeBarresError('Poids invalide (doit être > 0)', 'INVALID_WEIGHT');
    }
    if (typeof prixAuKg !== 'number' || !Number.isFinite(prixAuKg) || prixAuKg <= 0) {
        throw new CodeBarresError('Prix par kg invalide (doit être > 0)', 'INVALID_PRICE_PER_KG');
    }
    return Math.round(poidsKg * prixAuKg * 100);
}

/**
 * Formes equivalentes d'un code lu, a chercher dans le catalogue, dans
 * l'ordre : le code tel quel, puis UPC-A <-> EAN-13 (un 0 devant). Un lecteur
 * peut envoyer l'une ou l'autre forme du meme article.
 */
export function formesDuCode(code) {
    const formes = [code];
    if (/^\d{12}$/.test(code)) formes.push(`0${code}`);
    if (/^0\d{12}$/.test(code)) formes.push(code.slice(1));
    return formes;
}

export const TYPES_SCAN = Object.freeze({
    POIDS: 'POIDS',
    POIDS_ILLISIBLE: 'POIDS_ILLISIBLE',
    CODE: 'CODE',
    VIDE: 'VIDE',
});

/**
 * Classe un code lu (sans consulter le catalogue).
 *   POIDS : etiquette au poids lisible (prefixe, cle GS1 juste, prix > 0)
 *   POIDS_ILLISIBLE : prefixe au poids, 13 chiffres, mais cle fausse ou prix nul
 *   CODE : tout le reste, a chercher par sku (formesDuCode)
 * @param {string} texte
 * @param {{ prefixe?: string|null }} [options] prefixe du catalogue ; null = pas d'etiquette au poids
 */
export function classerScan(texte, { prefixe = null } = {}) {
    const code = typeof texte === 'string' ? texte.trim() : '';
    if (code === '') return { type: TYPES_SCAN.VIDE, code };
    const poids = prefixe ? lireEan13PrixIntegre(code, prefixe) : null;
    if (poids) {
        if (!poids.valid || poids.priceCents < PRIX_MIN_CENTS) return { type: TYPES_SCAN.POIDS_ILLISIBLE, code };
        return { type: TYPES_SCAN.POIDS, code, weightSku: poids.weightSku, priceCents: poids.priceCents };
    }
    return { type: TYPES_SCAN.CODE, code, formes: formesDuCode(code) };
}
